import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import {
  getCalendarDelegateGrantsApi,
  grantCalendarDelegateAccessApi,
  revokeCalendarDelegateAccessApi,
} from "../../../components/api/TravelCalendarApiService";

/**
 * Dialog for managing which users can edit the current user's travel calendar.
 *
 * Props:
 *   open           – boolean
 *   onClose        – () => void
 *   users          – all User[] from the app (for the "add delegate" dropdown)
 *   currentUserId  – the logged-in user's id (excluded from selection)
 */
const DelegateManagementDialog = ({ open, onClose, users, currentUserId }) => {
  const [grants, setGrants] = useState([]);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [selectedDelegateId, setSelectedDelegateId] = useState("");
  const [granting, setGranting] = useState(false);

  const loadGrants = () => {
    setLoadingGrants(true);
    getCalendarDelegateGrantsApi()
      .then((res) => setGrants(res?.data || []))
      .catch(() => toast.error("Failed to load delegates."))
      .finally(() => setLoadingGrants(false));
  };

  useEffect(() => {
    if (open) {
      loadGrants();
      setSelectedDelegateId("");
    }
  }, [open]);

  const grantedUserIds = new Set(grants.map((g) => g.userId));

  const availableUsers = (users || []).filter(
    (u) =>
      u.id !== currentUserId &&
      !grantedUserIds.has(u.id) &&
      (!u.recordStatus || u.recordStatus === "ACTIVE")
  );

  const handleGrant = () => {
    if (!selectedDelegateId) return;
    setGranting(true);
    grantCalendarDelegateAccessApi(selectedDelegateId)
      .then(() => {
        toast.success("Access granted.");
        setSelectedDelegateId("");
        loadGrants();
      })
      .catch(() => toast.error("Failed to grant access."))
      .finally(() => setGranting(false));
  };

  const handleRevoke = (userId, userName) => {
    if (!window.confirm(`Revoke calendar access for ${userName}?`)) return;
    revokeCalendarDelegateAccessApi(userId)
      .then(() => {
        toast.success("Access revoked.");
        loadGrants();
      })
      .catch(() => toast.error("Failed to revoke access."));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          backgroundColor: "#1E2685",
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleIcon />
          <Typography variant="h6" component="span">
            Manage Calendar Delegates
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
          Delegates can <strong>add, edit, and delete</strong> entries on your calendar. They cannot approve/disapprove entries or manage delegates themselves.
        </Typography>

        {/* ── Add new delegate ── */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          Add a delegate
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Select user</InputLabel>
            <Select
              value={selectedDelegateId}
              onChange={(e) => setSelectedDelegateId(e.target.value)}
              label="Select user"
            >
              {availableUsers.length === 0 ? (
                <MenuItem disabled value="">
                  No users available
                </MenuItem>
              ) : (
                availableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name || u.username}
                    {u.name && u.username !== u.name && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({u.username})
                      </Typography>
                    )}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={granting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <PersonAddIcon />}
            onClick={handleGrant}
            disabled={!selectedDelegateId || granting}
            sx={{ minWidth: 100, whiteSpace: "nowrap" }}
          >
            Grant
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Current delegates ── */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          Users with access to your calendar&nbsp;
          <Typography component="span" variant="body2" color="text.secondary">
            ({grants.length})
          </Typography>
        </Typography>

        {loadingGrants ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : grants.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No delegates yet. Add someone above to let them edit your calendar.
          </Typography>
        ) : (
          <List dense sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
            {grants.map((g, idx) => {
              const displayName = g.name || g.username || "Unknown";
              return (
                <React.Fragment key={g.grantId}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <Tooltip title="Revoke access" arrow>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => handleRevoke(g.userId, displayName)}
                        >
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 34, height: 34, fontSize: "0.85rem", bgcolor: "#1E2685" }}>
                        {displayName[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={g.name ? g.username : null}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DelegateManagementDialog;
